/**
 * Build the canonical PUT body for /api/profiles/:id from a profile object
 * plus optional overrides. Centralised so toggle handlers don't accidentally
 * reset newer fields (grid_density, night_tiles, school_*, etc.) to their
 * Pydantic defaults when only one field is being changed.
 *
 * Extracted from Board.jsx to keep that file under control.
 */
export function serializeProfile(profile, overrides = {}) {
  if (!profile) return null;
  const p = { ...profile, ...overrides };
  return {
    name: p.name,
    avatar: p.avatar,
    color: p.color,
    voice_rate: p.voice_rate,
    voice_pitch: p.voice_pitch,
    voice_name: p.voice_name || null,
    tile_size: p.tile_size,
    grid_density: p.grid_density || 4,
    ai_suggestions: p.ai_suggestions !== false,
    language: p.language || "en",
    first_word_celebrated: !!p.first_word_celebrated,
    dwell_ms: p.dwell_ms || 0,
    scan_mode: !!p.scan_mode,
    scan_interval_ms: p.scan_interval_ms || 1500,
    onboarded: !!p.onboarded,
    high_contrast: !!p.high_contrast,
    reduced_motion: !!p.reduced_motion,
    lockdown: !!p.lockdown,
    modeling_mode: !!p.modeling_mode,
    kid_mode: p.kid_mode !== false,
    age_pack: p.age_pack || "mixed",
    day_start: p.day_start || "07:00",
    night_start: p.night_start || "20:00",
    mode_override: p.mode_override || "auto",
    night_tiles: Array.isArray(p.night_tiles) ? p.night_tiles : [],
    school_start: p.school_start || "08:00",
    school_end: p.school_end || "15:00",
    school_mode_override: p.school_mode_override || "auto",
    // P1 — board growth level. CRITICAL: must be passed through verbatim
    // (including null for grandfathered profiles) so an unrelated board
    // toggle doesn't accidentally reset the level to the Pydantic default
    // and silently strip the cap + Sentence Builder gating.
    board_level: p.board_level ?? null,
    // P1 — Manage Board → School Settings
    school_contact: p.school_contact ?? null,
    allow_staff_access_requests: p.allow_staff_access_requests !== false,
    sentence_builder_enabled: p.sentence_builder_enabled === true,
  };
}

/**
 * Tailwind grid-cols class for board grid density (2..5). Drops density by
 * 1 in night mode to surface bedtime vocab in bigger, easier-to-tap chunks.
 */
const DENSITY_GRID = {
  2: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
  5: "grid-cols-3 sm:grid-cols-4 lg:grid-cols-5",
};

export function gridColsFor(rawDensity, mode) {
  const d = Number(rawDensity) || 4;
  const adjusted = Math.max(2, Math.min(5, mode === "night" ? d - 1 : d));
  return DENSITY_GRID[adjusted] || DENSITY_GRID[4];
}
